// What the replace flows agree on: the keyboard modifier that arms a replace, and the geometry
// and visual props that make one canvas item take over another item's slot.
//
// Two flows share this — dragging a thumb in from the side panel (which places a preloaded copy
// of the incoming item) and Cmd/Ctrl-dragging an item that is already on the canvas. Both need
// the incoming item to end up covering the target's rendered footprint, cropped to the target's
// aspect ratio and wearing the target's look, so the swap reads as a content change rather than
// a new item landing on top.

// ═══════════════════════════════════════════════════════════════════════════════
// The modifier that arms a replace
// ═══════════════════════════════════════════════════════════════════════════════

const IS_MAC = /Mac|iPhone|iPod|iPad/i.test(navigator.platform || navigator.userAgent);

// The key the shortcut is taught and labelled by: Cmd on Mac, Ctrl everywhere else.
export const MODIFIER_LABEL = IS_MAC ? 'Cmd' : 'Ctrl';

// Held mid-drag, a drop replaces the item underneath instead of landing on top of it.
export function isModifierHeld(event) {
  return IS_MAC ? event.metaKey : event.ctrlKey;
}

// Only the modifier's own key events say anything about whether it is held — every other key
// event merely carries its current state along, so a Cmd+Z mid-drag reports metaKey true and
// would otherwise arm a replace the user never asked for.
export function isModifierKey(event) {
  return event.key === (IS_MAC ? 'Meta' : 'Control');
}

// The look of a live replace preview's outline, shared so both flows preview identically.
export const PREVIEW_OUTLINE_CLASSES = 'fill-df-blue/15 stroke-df-blue stroke-2';

// What a replace is allowed to land on. Text has no footprint to crop into the target's shape,
// and a locked item is not the user's to swap out. Both flows hit-test with this, so neither can
// start offering a target the other refuses.
export const REPLACEABLE_TARGET = '.item:not(.item--text):not(.item--locked)';

// One place to report a completed replace, so the two flows cannot drift into how they announce
// it. Each passes its own feature-action name — the two flows are counted separately, never
// together — and the name must exist in config/settings/feature_actions/moodboard.yml or the
// server drops it silently.
export function recordReplace(featureAction) {
  window.ed.featureActionsService.add(featureAction);
  document.dispatchEvent(new CustomEvent('itemReplaced'));
}

// ═══════════════════════════════════════════════════════════════════════════════
// Taking over the target's slot
// ═══════════════════════════════════════════════════════════════════════════════

// The props that make the incoming item wear the target's look. Mirroring and capturing read the
// same list so a live preview can always be undone exactly.
const MIRRORED_PROPS = [
  'dropShadowHorizontalOffset',
  'dropShadowVerticalOffset',
  'dropShadowOpacity',
  'dropShadowEnabled',
  'dropShadowColor',
  'dropShadowBlur',
  'transparency',
  'brightness',
  'saturate',
  'contrast',
  'groupId',
  'round',
  'blur',
  'z',
];

// Everything the incoming item needs to get back to how it looked before it was fitted to a
// target — the previews are live, so releasing the modifier or hovering off has to undo them.
export function capturePlacement(item) {
  return {
    styleHeight: item.style.height,
    styleWidth: item.style.width,
    transform: item.e._objProps.transform,
    cropLeft: item.e.cropLeft || 0,
    cropTop: item.e.cropTop || 0,
    height: item.e._objProps.height,
    width: item.e._objProps.width,
    realX: item.e._objProps.realX,
    realY: item.e._objProps.realY,
  };
}

export function restorePlacement(item, snapshot) {
  if (!snapshot) {
    return false;
  }
  item.e._objProps.transform = snapshot.transform;
  item.e._objProps.height = snapshot.height;
  item.e._objProps.width = snapshot.width;
  item.e._objProps.realX = snapshot.realX;
  item.e._objProps.realY = snapshot.realY;
  item.e.cropLeft = snapshot.cropLeft;
  item.e.cropTop = snapshot.cropTop;
  item.style.height = snapshot.styleHeight;
  item.style.width = snapshot.styleWidth;
  window.ed.updateSingleItemZoomRatio(item, false);

  return true;
}

export function mirrorTargetVisuals(item, target) {
  const targetProps = target.e._objProps;
  MIRRORED_PROPS.forEach((key) => {
    item.e._objProps[key] = targetProps[key];
  });
  item.style.zIndex = `${targetProps.z}`;
  item.style.filter = item.e.buildFilter();
  item.classList.toggle('item--round', Boolean(targetProps.round));
  if (target.dataset.groupId) {
    item.dataset.groupId = target.dataset.groupId;
  } else {
    item.removeAttribute('data-group-id');
  }
}

// The counterpart to mirrorTargetVisuals, for flows that preview with the real item rather than a
// throwaway copy: without this, backing out of a preview would leave the item wearing the look of
// an item it never replaced.
export function captureVisuals(item) {
  const props = {};
  MIRRORED_PROPS.forEach((key) => {
    props[key] = item.e._objProps[key];
  });

  return {
    props,
    zIndex: item.style.zIndex,
    filter: item.style.filter,
    isRound: item.classList.contains('item--round'),
    groupId: item.dataset.groupId,
  };
}

export function restoreVisuals(item, snapshot) {
  if (!snapshot) {
    return;
  }
  Object.assign(item.e._objProps, snapshot.props);
  item.style.zIndex = snapshot.zIndex;
  item.style.filter = snapshot.filter;
  item.classList.toggle('item--round', snapshot.isRound);
  // An item with no group has no attribute at all, which is not the same as an empty one — the
  // group selection logic reads presence, so put it back the way it was found.
  if (snapshot.groupId === undefined) {
    item.removeAttribute('data-group-id');
  } else {
    item.dataset.groupId = snapshot.groupId;
  }
}

export function fitToTargetBounds(item, target) {
  const targetProps = target.e._objProps;
  const oldWidth = targetProps.width;
  const oldHeight = targetProps.height;
  const newWidth = item.e._objProps.width;
  const newHeight = item.e._objProps.height;

  applyScaledTransform(item, targetProps.transform, oldWidth, oldHeight, newWidth, newHeight);

  // center item over target's center
  item.e._objProps.realX = targetProps.realX + (oldWidth - newWidth) / 2;
  item.e._objProps.realY = targetProps.realY + (oldHeight - newHeight) / 2;
  window.ed.updateSingleItemZoomRatio(item, false);

  cropAndPosition(item, oldWidth, oldHeight);

  return redrawIfCanvas(item);
}

export function redrawIfCanvas(item) {
  if (item.sourceImage) {
    return window.ed._shared.drawItem(item, item.e);
  }

  return Promise.resolve();
}

function applyScaledTransform(item, existingTransform, oldWidth, oldHeight, newWidth, newHeight) {
  const uniformScale = Math.max(oldWidth / newWidth, oldHeight / newHeight);
  const transformStr = existingTransform ? `matrix3d(${existingTransform})` : 'none';
  const matrix = window.ed._matrixFunctions.parseCssTransform(transformStr);
  const scaleMatrix = [
    [uniformScale, 0, 0, 0],
    [0, uniformScale, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1],
  ];
  const result = window.ed._matrixFunctions.multiplyMatrices(matrix, scaleMatrix);
  const newTransform = flattenColumnMajor(result);
  item.e._objProps.transform = newTransform;
  item.e.transform = newTransform;
}

function flattenColumnMajor(matrix) {
  return matrix[0].map((_, c) => matrix.map((r) => r[c])).join(',');
}

function cropAndPosition(item, targetW, targetH) {
  const currentHeight = parseFloat(item.style.height);
  const currentWidth = parseFloat(item.style.width);
  const currentLeft = parseFloat(item.style.left);
  const currentTop = parseFloat(item.style.top);
  const zoom = window.ed.zoomRatio;
  const { width: cropWidth, height: cropHeight } = cropItem(
    currentWidth,
    currentHeight,
    targetW,
    targetH
  );
  const cropLeft = (currentWidth - cropWidth) / 2;
  const cropTop = (currentHeight - cropHeight) / 2;
  const newLeft = currentLeft + cropLeft;
  const newTop = currentTop + cropTop;

  item.e.cropLeft = (item.e.cropLeft || 0) + cropLeft;
  item.e.cropTop = (item.e.cropTop || 0) + cropTop;

  Object.assign(item.style, {
    width: `${cropWidth}px`,
    height: `${cropHeight}px`,
    left: `${newLeft}px`,
    top: `${newTop}px`,
  });

  item.e._objProps.width = cropWidth;
  item.e._objProps.height = cropHeight;
  item.e._objProps.realX = newLeft / zoom - (cropWidth / 2) * ((zoom - 1) / zoom);
  item.e._objProps.realY = newTop / zoom - (cropHeight / 2) * ((zoom - 1) / zoom);
  item.e._objProps.x = newLeft;
  item.e._objProps.y = newTop;
}

function cropItem(itemW, itemH, targetW, targetH) {
  const targetRatio = targetW / targetH;
  const itemRatio = itemW / itemH;

  let width;
  let height;
  if (itemRatio > targetRatio) {
    // item is wider than target shape → height is the constraint
    height = itemH;
    width = Math.round(itemH * targetRatio);
  } else {
    // item is taller (or equal) → width is the constraint
    width = itemW;
    height = Math.round(itemW / targetRatio);
  }

  return { width, height };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Presentational stand-in for a committed fit
// ═══════════════════════════════════════════════════════════════════════════════
//
// fitToTargetBounds bakes the crop into the item's bitmap, which means a canvas re-encode: the
// box changes this frame and the pixels arrive several frames later, so the item renders at the
// wrong aspect ratio in between. Fine when the item can be hidden until the pixels land, useless
// for a preview the user is dragging around.
//
// So a preview borrows the target's box and lets `object-fit: cover` do the cropping — cover
// scales the bitmap to fill the box and clips the overflow from the centre, which is the same
// rectangle cropItem picks. Purely presentational: no re-encode, nothing async, and item props
// are left alone, so arming, retargeting and backing out are all single-frame style changes.
// The commit keeps the fill until its real crop has painted, so that swap is seamless too.

export function captureBox(item) {
  return {
    width: item.style.width,
    height: item.style.height,
    left: item.style.left,
    top: item.style.top,
    transform: item.style.transform,
    objectFit: item.style.objectFit,
  };
}

export function restoreBox(item, snapshot) {
  if (!snapshot) {
    return;
  }
  item.style.width = snapshot.width;
  item.style.height = snapshot.height;
  item.style.left = snapshot.left;
  item.style.top = snapshot.top;
  item.style.transform = snapshot.transform;
  item.style.objectFit = snapshot.objectFit;
}

// Read the target's rendered box rather than its inline styles: what has to be matched is where
// the target actually draws, and inline width/height are not set on every kind of item. Same
// defensiveness as Interaction#getOriginalCoordinates.
export function showAsTargetFootprint(item, target) {
  const computed = window.getComputedStyle(target);
  item.style.width = `${target.offsetWidth}px`;
  item.style.height = `${target.offsetHeight}px`;
  item.style.left = target.style.left || computed.left;
  item.style.top = target.style.top || computed.top;
  item.style.transform = target.style.transform || computed.transform;
  fillBox(item);
}

export function fillBox(item) {
  item.style.objectFit = 'cover';
}

export function clearBoxFill(item) {
  item.style.objectFit = '';
}
